import React, { useState, useEffect, useRef } from 'react';
import { GlassPanel } from 'liquidglass-react';

interface TileMetrics {
  tilesRendered: number;
  tilesLoading: number;
  tilesTotal: number;
  tilesKicked: number;
  notYetRenderableCount: number;
  allConverged: boolean;
  frameTimeMs: number;
  fps: number;
  drainUploadMs: number;
  translateTerrainMs: number;
  translateModelMs: number;
  renderFrameMs: number;
  bindGroupCreationMs: number;
  uploadBudgetMs: number;
  gpuTerrainPassMs: number;
  gpuModelPassMs: number;
  gpuSkyPassMs: number;
  gpuTotalPassMs: number;
  proxyLatencyMs: number;
  tileSelectionMs: number;
  visualDebtScore: number;
}

interface TileMetricsOverlayProps {
  renderer: any;
  visible: boolean;
}

const TileMetricsOverlay: React.FC<TileMetricsOverlayProps> = ({ renderer, visible }) => {
  const [metrics, setMetrics] = useState<TileMetrics | null>(null);
  const [convergenceTime, setConvergenceTime] = useState<number | null>(null);
  const convergenceStartRef = useRef<number | null>(null);
  const prevConvergedRef = useRef<boolean>(true);

  useEffect(() => {
    if (!visible || !renderer) return;

    const poll = () => {
      try {
        if (typeof renderer.getTileMetrics !== 'function') {
          return;
        }
        const m: TileMetrics = renderer.getTileMetrics();
        setMetrics(m);

        // Track convergence timing
        if (!m.allConverged && prevConvergedRef.current) {
          // Transition from converged to loading: start timer
          convergenceStartRef.current = performance.now();
          setConvergenceTime(null);
        } else if (m.allConverged && !prevConvergedRef.current) {
          // Transition from loading to converged: capture final time
          if (convergenceStartRef.current !== null) {
            setConvergenceTime(Math.round(performance.now() - convergenceStartRef.current));
          }
        } else if (!m.allConverged && convergenceStartRef.current !== null) {
          // Still loading: update elapsed time
          setConvergenceTime(Math.round(performance.now() - convergenceStartRef.current));
        }

        prevConvergedRef.current = m.allConverged;
      } catch {
        // getTileMetrics may not exist until WASM is rebuilt
      }
    };

    const interval = setInterval(poll, 250);
    poll(); // Initial poll

    return () => clearInterval(interval);
  }, [visible, renderer]);

  if (!visible) return null;

  const fpsStr = metrics ? metrics.fps.toFixed(1) : '0.0';
  const rendered = metrics ? metrics.tilesRendered : 0;
  const total = metrics ? metrics.tilesTotal : 0;
  const loading = metrics ? metrics.tilesLoading : 0;
  const kicked = metrics ? metrics.tilesKicked : 0;
  const converged = metrics ? metrics.allConverged : false;
  const timeStr = convergenceTime !== null ? `${convergenceTime}ms` : '---';

  // Per-phase CPU timing
  const drainMs = metrics ? metrics.drainUploadMs.toFixed(1) : '0.0';
  const terrainMs = metrics ? metrics.translateTerrainMs.toFixed(1) : '0.0';
  const modelMs = metrics ? metrics.translateModelMs.toFixed(1) : '0.0';
  const renderMs = metrics ? metrics.renderFrameMs.toFixed(1) : '0.0';
  const bindGrpMs = metrics ? metrics.bindGroupCreationMs.toFixed(1) : '0.0';
  const uploadBudget = metrics ? metrics.uploadBudgetMs.toFixed(1) : '3.0';
  const gpuTotal = metrics ? metrics.gpuTotalPassMs : -1;
  const gpuStr = gpuTotal >= 0 ? `\nGPU: ${gpuTotal.toFixed(1)}ms` : '';
  const proxyMs = metrics ? metrics.proxyLatencyMs.toFixed(1) : '0.0';
  const selectionMs = metrics?.tileSelectionMs != null ? metrics.tileSelectionMs.toFixed(1) : '0.0';
  const debtScore = metrics ? metrics.visualDebtScore.toFixed(3) : '0.000';

  return (
    <GlassPanel
      cornerRadius={12}
      style={{
        position: 'fixed',
        top: 8,
        left: 8,
        zIndex: 9999,
        color: '#0f0',
        fontFamily: 'monospace',
        fontSize: 12,
        padding: '4px 8px',
        pointerEvents: 'none',
        lineHeight: 1.5,
        whiteSpace: 'pre',
        textShadow: 'none',
      }}
    >
      {`Tiles: ${rendered} / ${total}\nLoading: ${loading} | Kicked: ${kicked}\nFPS: ${fpsStr}\nSelect: ${selectionMs}ms | Drain: ${drainMs}ms | Debt: ${debtScore}\nTerrain: ${terrainMs}ms | Model: ${modelMs}ms\nRender: ${renderMs}ms | BindGrp: ${bindGrpMs}ms | rAF: ${proxyMs}ms\nUpload Budget: ${uploadBudget}ms${gpuStr}\n${converged ? `CONVERGED (${timeStr})` : `LOADING... (${timeStr})`}`}
    </GlassPanel>
  );
};

export default TileMetricsOverlay;
